/**
 * The pharmacy till.
 *
 * The screen proposes; the server decides. Which batches a sale draws from,
 * whether a prescription-only medicine may go, whether a discount is allowed
 * and whether a card payment counts as paid are all settled by the API — the
 * warnings here only save the cashier a refusal.
 *
 * One idempotency key per attempt: it is made when the cart changes and kept
 * across retries of the same cart, so a checkout that timed out and is pressed
 * again records one sale, not two.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Minus, PauseCircle, Plus, Printer, ScanLine, Trash2, WifiOff } from 'lucide-react';
import {
  PAYMENT_METHODS,
  PAYMENT_METHOD_LABELS,
  addAmounts,
  compareAmounts,
  loyaltyValueOf,
  multiplyPrice,
  subtractAmounts,
  couldBeBarcode,
  isNumericBarcode,
  type PaymentMethod,
} from '@cenox/shared';
import { api, qs } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { useWorkingLocation } from '../../lib/locationContext';
import { dateTime, money } from '../../lib/format';
import { TRANSFER_REFERENCE_HINT, defaultUnit, needsTransferReference, tillStateLabel, tillStateTone, transferReferenceReady, type TillItem } from '../../lib/till';
import { PendingDecision, type PendingDecisionBody } from './PendingDecision';
import { Badge, Button, Card, CardHeader, Input, Modal, Select, cn } from '../../components/ui';
import { PageHeader } from '../../layouts/AppShell';
import { newKey, usePharmacy, usePharmacyWrite, type CustomerLoyalty, type ListOf, type MedicineListItem, type PharmacySettings, type UnitView } from './data';
import { ErrorLine, RxBadge, StatusBadge, Td, Table, describeQuantity } from './parts';

interface CartLine {
  key: string;
  productId: string;
  productName: string;
  rxClass: string;
  unitId: string;
  unitName: string;
  unitPrice: string;
  quantity: number;
  discount: string;
  /** Unknown (a held cart, a prescription line) counts as a medicine: no points on it. */
  isMedicine?: boolean;
  prescriptionItemId?: string;
}

interface Tender {
  method: PaymentMethod;
  amount: string;
  reference: string;
  confirmed: boolean;
}

interface CheckoutResult {
  saleId: string;
  reference: string;
  amount: string;
  amountPaid: string;
  pendingAmount: string;
  outstanding: string;
  changeDue: string;
  paymentStatus: string;
  duplicate: boolean;
  pendingPaymentIds: string[];
}

interface Receipt {
  loyalty_discount?: string;
  pointsRedeemed?: number;
  pointsEarned?: number;
  reference: string;
  occurredAt: string;
  currencyCode: string;
  amount: string;
  amountPaid: string;
  outstanding: string;
  paymentStatus: string;
  customerName: string | null;
  business_name: string;
  branch_name: string;
  branch_address: string | null;
  branch_phone: string | null;
  cashier: string | null;
  gross: string;
  discount: string;
  prescription_reference: string | null;
  lines: Array<{ sale_item_id: string; description: string; unit_name: string; unit_quantity: number; unit_price: string; discount: string; line_total: string }>;
  payments: Array<{ method: string; amount: string; paid_at: string }>;
  pendingPayments: Array<{ id: string; method: string; amount: string; status: string }>;
  refunds: Array<{ reference: string; amount: string }>;
}

interface Prescription {
  id: string;
  reference: string;
  patientName: string;
  patientAllergies: string[];
  dispensingStatus: string;
  reviewedByName: string | null;
  items: Array<{
    id: string;
    productId: string;
    productName: string;
    rxClass: string;
    baseUnitName: string;
    approvedQuantity: number | null;
    approvedDirections: string | null;
    outstandingQuantity: number | null;
  }>;
}

interface Till {
  id: string;
  status: string;
  opened_at: string;
  opening_float: string;
  cashier: string;
  cashier_user_id: string;
}

const TENDER_METHODS = PAYMENT_METHODS.filter((m) => m !== 'credit');
const lineKey = (unitId: string, rxItem?: string) => `${unitId}:${rxItem ?? ''}`;

function useOnline(): boolean {
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);
  return online;
}

function useDebounced<T>(value: T, ms = 150): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function PosPage(): ReactNode {
  const { can, business } = useAuth();
  const currency = business?.currencyCode ?? 'NGN';
  const timezone = business?.timezone ?? 'UTC';
  const { branchId: selected, defaultBranchId, locations } = useWorkingLocation();
  const [picked, setPicked] = useState<string | null>(null);
  const branchId = picked ?? selected ?? defaultBranchId ?? locations[0]?.id ?? null;
  const online = useOnline();
  const [params, setParams] = useSearchParams();
  const rxId = params.get('rx');

  const base = useBusinessPath();
  const [search, setSearch] = useState('');
  const term = useDebounced(search).trim();
  // Suggestions from the second letter typed: the whole catalogue, classified
  // or not, each saying whether it can be sold here.
  const searching = Boolean(branchId && term.length >= 2);
  const results = usePharmacy<ListOf<TillItem>>('/till/items', { search: term, branchId, limit: 20 }, searching);
  const suggestions = searching ? (results.data?.data ?? []) : [];
  const [highlight, setHighlight] = useState(0);
  useEffect(() => setHighlight(0), [term]);
  const [notice, setNotice] = useState<string | null>(null);

  const [cart, setCart] = useState<CartLine[]>([]);
  const [basketDiscount, setBasketDiscount] = useState('');
  const [discountReason, setDiscountReason] = useState('');
  const [tenders, setTenders] = useState<Tender[]>([{ method: 'cash', amount: '', reference: '', confirmed: false }]);
  const [credit, setCredit] = useState(false);
  const [customer, setCustomer] = useState<CustomerChoice>(WALK_IN);
  const [redeem, setRedeem] = useState('');
  const [attemptKey, setAttemptKey] = useState(newKey);
  const [done, setDone] = useState<CheckoutResult | null>(null);
  const [holdOpen, setHoldOpen] = useState(false);
  const [scanMiss, setScanMiss] = useState<string | null>(null);

  const rx = usePharmacy<Prescription>(`/prescriptions/${rxId}`, {}, Boolean(rxId && can('prescriptions:read')));

  // A changed cart is a new attempt; an unchanged one keeps its key across retries.
  // Who is buying is deliberately not part of it: correcting the customer
  // after a checkout that timed out must not take the money a second time —
  // the retry returns the sale that already went through.
  useEffect(() => {
    setAttemptKey(newKey());
  }, [cart, basketDiscount, tenders, credit, redeem]);

  const checkout = usePharmacyWrite<Record<string, unknown>, CheckoutResult>('post', '/checkout');

  // Loyalty points: offered only when the owner has switched redemption on
  // and the buyer is a customer on file. The server decides; this previews.
  const settings = usePharmacy<PharmacySettings>('/settings');
  const redemptionOn = Boolean(settings.data?.loyaltyRedemptionEnabled);
  const loyalty = usePharmacy<CustomerLoyalty>(
    customer.kind === 'existing' ? `/loyalty/customers/${customer.id}` : '/loyalty/none',
    {},
    redemptionOn && customer.kind === 'existing' && can('customers:read'),
  );
  const nonMedicineInCart = cart.some((l) => l.isMedicine === false);
  // Only what the field on screen shows is sent: no points without a non-medicine
  // to spend them on, or beyond what this customer holds.
  const redeemPoints =
    redemptionOn && customer.kind === 'existing' && nonMedicineInCart && /^\d+$/.test(redeem)
      ? Math.min(Number(redeem), Math.max(loyalty.data?.balance ?? 0, 0))
      : 0;
  // Points typed for one customer are never spent from another's.
  const customerKey = customer.kind === 'existing' ? customer.id : customer.kind;
  useEffect(() => setRedeem(''), [customerKey]);

  function addUnit(
    item: Pick<MedicineListItem, 'id' | 'name' | 'rxClass'> & { isMedicine?: boolean },
    unit: Pick<UnitView, 'id' | 'name' | 'price'>,
    rxItemId?: string,
  ) {
    if (unit.price === null) return;
    const k = lineKey(unit.id, rxItemId);
    setCart((lines) => {
      const found = lines.find((l) => l.key === k);
      if (found) return lines.map((l) => (l.key === k ? { ...l, quantity: l.quantity + 1 } : l));
      return [
        ...lines,
        {
          key: k,
          productId: item.id,
          productName: item.name,
          rxClass: item.rxClass,
          isMedicine: item.isMedicine ?? true,
          unitId: unit.id,
          unitName: unit.name,
          unitPrice: unit.price!,
          quantity: 1,
          discount: '',
          ...(rxItemId ? { prescriptionItemId: rxItemId } : {}),
        },
      ];
    });
  }

  /** Put an item in the cart, or say why it cannot go. */
  function take(item: TillItem): boolean {
    const unit = item.sellable ? defaultUnit(item) : null;
    if (!unit || !item.rxClass) {
      setNotice(item.reason ?? `${item.name} cannot be sold here.`);
      return false;
    }
    addUnit({ id: item.id, name: item.name, rxClass: item.rxClass, isMedicine: item.isMedicine ?? true }, unit);
    setSearch('');
    setScanMiss(null);
    setNotice(null);
    return true;
  }

  /**
   * Enter: a scan or a pick.
   *
   * A scanner types a code and presses Enter at once, before any suggestion
   * has loaded, so a single token is first looked up as an exact barcode at
   * the server. Only an all-digit code that matches nothing is reported as an
   * unknown barcode; a word such as "perfume" falls through to the
   * highlighted suggestion and is never treated as a scan.
   */
  async function onSearchKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!suggestions.length) return;
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setHighlight((h) => (h + step + suggestions.length) % suggestions.length);
      return;
    }
    if (e.key === 'Escape') {
      setSearch('');
      setNotice(null);
      setScanMiss(null);
      return;
    }
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const raw = search.trim();
    if (!raw || !branchId) return;
    setScanMiss(null);
    setNotice(null);
    if (couldBeBarcode(raw)) {
      const hit = await api
        .get<ListOf<TillItem>>(base(`/pharmacy/till/items${qs({ barcode: raw, branchId })}`))
        .then((r) => r.data[0] ?? null)
        .catch(() => null);
      if (hit) {
        take(hit);
        return;
      }
      if (isNumericBarcode(raw)) {
        setScanMiss(raw);
        return;
      }
    }
    // A typed word: the highlighted suggestion, once suggestions for it are in.
    const picked = term === raw ? suggestions[highlight] : undefined;
    if (picked) take(picked);
  }

  const totals = useMemo(() => {
    let gross = '0.00';
    let lineDiscounts = '0.00';
    for (const l of cart) {
      gross = addAmounts(gross, multiplyPrice(l.unitPrice, l.quantity));
      if (l.discount) lineDiscounts = addAmounts(lineDiscounts, l.discount);
    }
    const discount = addAmounts(lineDiscounts, basketDiscount || '0');
    const points = redeemPoints && settings.data ? loyaltyValueOf(redeemPoints, settings.data.loyaltyPointValue) : '0.00';
    return { gross, discount, points, total: subtractAmounts(subtractAmounts(gross, discount), points) };
  }, [cart, basketDiscount, redeemPoints, settings.data]);

  const tendered = useMemo(() => {
    let confirmed = '0.00';
    let pending = '0.00';
    let cash = '0.00';
    for (const t of tenders) {
      if (!t.amount || Number.isNaN(Number(t.amount))) continue;
      if (t.method === 'cash' || t.confirmed) confirmed = addAmounts(confirmed, t.amount);
      else pending = addAmounts(pending, t.amount);
      if (t.method === 'cash') cash = addAmounts(cash, t.amount);
    }
    const change = compareAmounts(confirmed, totals.total) > 0 ? subtractAmounts(confirmed, totals.total) : '0.00';
    const covered = addAmounts(confirmed, pending);
    const short = compareAmounts(covered, totals.total) < 0 ? subtractAmounts(totals.total, covered) : '0.00';
    return { confirmed, pending, cash, change, short };
  }, [tenders, totals.total]);

  const needsRx = cart.filter((l) => (l.rxClass === 'prescription_only' || l.rxClass === 'restricted') && !l.prescriptionItemId);

  function submit() {
    if (!branchId) return;
    checkout.mutate(
      {
        branchId,
        lines: cart.map((l) => ({
          unitId: l.unitId,
          quantity: l.quantity,
          ...(l.discount && Number(l.discount) > 0 ? { discount: l.discount } : {}),
          ...(l.prescriptionItemId ? { prescriptionItemId: l.prescriptionItemId } : {}),
        })),
        tenders: tenders
          .filter((t) => t.amount && Number(t.amount) > 0)
          .map((t) => ({
            method: t.method,
            amount: t.amount,
            ...(t.reference ? { reference: t.reference } : {}),
            confirmed: t.method === 'cash' ? true : t.confirmed,
          })),
        allowCredit: credit,
        ...customerPayload(customer),
        ...(redeemPoints > 0 ? { redeemPoints } : {}),
        ...(basketDiscount && Number(basketDiscount) > 0 ? { basketDiscount, discountReason: discountReason || null } : {}),
        ...(rxId ? { prescriptionId: rxId } : {}),
        idempotencyKey: attemptKey,
      },
      { onSuccess: (r) => setDone(r) },
    );
  }

  function newSale() {
    setDone(null);
    setCart([]);
    setBasketDiscount('');
    setDiscountReason('');
    setTenders([{ method: 'cash', amount: '', reference: '', confirmed: false }]);
    setCredit(false);
    setCustomer(WALK_IN);
    setRedeem('');
    checkout.reset();
    if (rxId) {
      params.delete('rx');
      setParams(params);
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="POS"
        description="Sell medicines, cosmetics and retail items. Batches are chosen earliest-expiry first by the server."
        action={
          <div className="flex flex-wrap items-center gap-2">
            {can('pharmacy:outage_entry') && (
              <Link to="/pharmacy/pos/outage" className="text-sm font-medium text-brand-700 hover:underline">
                Outage back-entry
              </Link>
            )}
            {branchId && <TillControl branchId={branchId} currency={currency} timezone={timezone} />}
          </div>
        }
      />

      {!online && (
        <div role="alert" className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm font-medium text-red-800">
          <WifiOff className="mt-0.5 size-4 shrink-0" aria-hidden />
          Offline — CenoX cannot verify stock, prescriptions or payments. Use the outage procedure; do not record sales here
          until back online.
        </div>
      )}

      {(!selected || locations.length > 1) && (
        <div className="max-w-xs">
          <Select
            label="Selling at"
            value={branchId ?? ''}
            placeholder="Choose a location"
            options={locations.map((l) => ({ value: l.id, label: l.name }))}
            onChange={(e) => setPicked(e.target.value || null)}
          />
        </div>
      )}

      {done ? (
        <ReceiptPanel result={done} currency={currency} timezone={timezone} onNewSale={newSale} />
      ) : (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_26rem]">
          <div className="space-y-5">
            {rxId && can('prescriptions:read') && (
              <PrescriptionPanel rx={rx.data} loading={rx.isPending} onAdd={(item, unit, rxItem) => addUnit(item, unit, rxItem)} branchId={branchId} />
            )}

            <Card>
              <div className="relative">
                <ScanLine className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400" aria-hidden />
                <input
                  autoFocus
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setScanMiss(null);
                    setNotice(null);
                  }}
                  onKeyDown={onSearchKey}
                  placeholder="Search by name, generic name, SKU or barcode — or scan"
                  aria-label="Search or scan"
                  role="combobox"
                  aria-expanded={suggestions.length > 0}
                  aria-controls="till-suggestions"
                  aria-autocomplete="list"
                  aria-activedescendant={suggestions[highlight] ? `till-option-${suggestions[highlight]!.id}` : undefined}
                  autoComplete="off"
                  className="input-base pl-9"
                  disabled={!branchId}
                />
              </div>
              {scanMiss && <p className="mt-2 text-sm text-amber-700">No item has the barcode {scanMiss}.</p>}
              {notice && (
                <p role="status" className="mt-2 text-sm text-amber-700">
                  {notice}
                </p>
              )}
              {search.trim().length === 1 && <p className="mt-2 text-xs text-slate-500">Keep typing to see suggestions.</p>}
              {searching && (
                <div className="mt-3">
                  {results.isPending ? (
                    <p className="py-4 text-sm text-slate-500">Searching…</p>
                  ) : suggestions.length === 0 ? (
                    <p className="py-4 text-sm text-slate-500">Nothing in the catalogue matches “{term}”.</p>
                  ) : (
                    <ul id="till-suggestions" role="listbox" aria-label="Suggestions" className="divide-y divide-slate-100">
                      {suggestions.map((item, i) => (
                        <TillSuggestion
                          key={item.id}
                          item={item}
                          active={i === highlight}
                          currency={currency}
                          onHover={() => setHighlight(i)}
                          onAdd={(unit) =>
                            item.rxClass &&
                            addUnit({ id: item.id, name: item.name, rxClass: item.rxClass, isMedicine: item.isMedicine ?? true }, unit)
                          }
                        />
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </Card>

            {can('pharmacy:sell') && branchId && <HeldCarts branchId={branchId} onResume={(lines) => setCart(lines)} />}
          </div>

          <Card className="h-fit lg:sticky lg:top-6">
            <CardHeader
              title="Cart"
              subtitle={cart.length ? `${cart.length} line${cart.length === 1 ? '' : 's'}` : 'Empty'}
              action={
                cart.length ? (
                  <Button size="sm" variant="ghost" icon={<PauseCircle className="size-4" />} onClick={() => setHoldOpen(true)}>
                    Hold
                  </Button>
                ) : undefined
              }
            />
            {cart.length === 0 ? (
              <p className="py-6 text-center text-sm text-slate-500">Search or scan to add items.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {cart.map((l) => (
                  <li key={l.key} className="py-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate font-medium text-slate-900">{l.productName}</p>
                        <p className="text-xs text-slate-500">
                          {l.unitName} at {money(l.unitPrice, currency)}
                          {l.prescriptionItemId && <> · <Badge tone="info">Rx</Badge></>}
                        </p>
                      </div>
                      <p className="font-medium tabular-nums">{money(multiplyPrice(l.unitPrice, l.quantity), currency)}</p>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <div className="inline-flex items-center rounded-lg border border-slate-200">
                        <button
                          type="button"
                          aria-label="One fewer"
                          className="p-1.5 text-slate-600 hover:bg-slate-50"
                          onClick={() =>
                            setCart((c) => c.map((x) => (x.key === l.key ? { ...x, quantity: Math.max(1, x.quantity - 1) } : x)))
                          }
                        >
                          <Minus className="size-4" />
                        </button>
                        <input
                          aria-label="Quantity"
                          inputMode="numeric"
                          className="w-12 border-x border-slate-200 py-1 text-center text-sm tabular-nums"
                          value={l.quantity}
                          onChange={(e) => {
                            const n = Number.parseInt(e.target.value.replace(/\D/g, ''), 10);
                            setCart((c) => c.map((x) => (x.key === l.key ? { ...x, quantity: Number.isFinite(n) && n > 0 ? n : 1 } : x)));
                          }}
                        />
                        <button
                          type="button"
                          aria-label="One more"
                          className="p-1.5 text-slate-600 hover:bg-slate-50"
                          onClick={() => setCart((c) => c.map((x) => (x.key === l.key ? { ...x, quantity: x.quantity + 1 } : x)))}
                        >
                          <Plus className="size-4" />
                        </button>
                      </div>
                      {can('pharmacy:discount') && (
                        <input
                          aria-label="Line discount"
                          placeholder="Discount"
                          inputMode="decimal"
                          className="input-base h-8 w-24 text-sm"
                          value={l.discount}
                          onChange={(e) => setCart((c) => c.map((x) => (x.key === l.key ? { ...x, discount: e.target.value } : x)))}
                        />
                      )}
                      <button
                        type="button"
                        aria-label={`Remove ${l.productName}`}
                        className="ml-auto rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"
                        onClick={() => setCart((c) => c.filter((x) => x.key !== l.key))}
                      >
                        <Trash2 className="size-4" />
                      </button>
                    </div>
                    {(l.rxClass === 'prescription_only' || l.rxClass === 'restricted') && !l.prescriptionItemId && (
                      <p className="mt-1.5 flex items-center gap-1 text-xs text-amber-700">
                        <AlertTriangle className="size-3.5" aria-hidden />
                        Prescription-only. Dispense it from an approved prescription; the sale will be refused otherwise.
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {cart.length > 0 && (
              <div className="mt-3 space-y-3 border-t border-slate-200 pt-3">
                {can('pharmacy:discount') && (
                  <div className="grid grid-cols-2 gap-2">
                    <Input label="Basket discount" inputMode="decimal" value={basketDiscount} onChange={(e) => setBasketDiscount(e.target.value)} />
                    <Input label="Reason" value={discountReason} onChange={(e) => setDiscountReason(e.target.value)} />
                  </div>
                )}
                <dl className="space-y-1 text-sm">
                  <Row label="Gross" value={money(totals.gross, currency)} />
                  {Number(totals.discount) > 0 && <Row label="Discount" value={`− ${money(totals.discount, currency)}`} />}
                  {Number(totals.points) > 0 && <Row label={`${redeemPoints} loyalty points`} value={`− ${money(totals.points, currency)}`} />}
                  <Row label="Total" value={money(totals.total, currency)} strong />
                </dl>

                <fieldset className="space-y-2">
                  <legend className="text-sm font-medium text-slate-700">Payment</legend>
                  {tenders.map((t, i) => (
                    <div key={i} className="space-y-1.5 rounded-lg border border-slate-200 p-2">
                      <div className="flex gap-2">
                        <select
                          aria-label="Method"
                          className="input-base h-9 flex-1"
                          value={t.method}
                          onChange={(e) =>
                            setTenders((ts) => ts.map((x, j) => (j === i ? { ...x, method: e.target.value as PaymentMethod, confirmed: false } : x)))
                          }
                        >
                          {TENDER_METHODS.map((m) => (
                            <option key={m} value={m}>
                              {PAYMENT_METHOD_LABELS[m]}
                            </option>
                          ))}
                        </select>
                        <input
                          aria-label="Amount"
                          inputMode="decimal"
                          placeholder={i === 0 ? totals.total : 'Amount'}
                          className="input-base h-9 w-28 tabular-nums"
                          value={t.amount}
                          onChange={(e) => setTenders((ts) => ts.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)))}
                        />
                        {tenders.length > 1 && (
                          <button
                            type="button"
                            aria-label="Remove payment"
                            className="rounded p-1.5 text-slate-400 hover:text-red-600"
                            onClick={() => setTenders((ts) => ts.filter((_, j) => j !== i))}
                          >
                            <Trash2 className="size-4" />
                          </button>
                        )}
                      </div>
                      {t.method !== 'cash' && (
                        <>
                          <input
                            aria-label="Reference"
                            placeholder="Terminal or transfer reference"
                            className="input-base h-8 text-sm"
                            value={t.reference}
                            onChange={(e) => setTenders((ts) => ts.map((x, j) => (j === i ? { ...x, reference: e.target.value } : x)))}
                          />
                          {needsTransferReference(t.method) && <p className="text-xs text-slate-500">{TRANSFER_REFERENCE_HINT}</p>}
                          <label className="flex items-start gap-2 text-xs text-slate-600">
                            <input
                              type="checkbox"
                              className="mt-0.5"
                              checked={t.confirmed}
                              onChange={(e) => setTenders((ts) => ts.map((x, j) => (j === i ? { ...x, confirmed: e.target.checked } : x)))}
                            />
                            I have seen the approval (terminal slip / bank alert)
                          </label>
                        </>
                      )}
                    </div>
                  ))}
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<Plus className="size-4" />}
                    onClick={() => setTenders((ts) => [...ts, { method: 'card', amount: '', reference: '', confirmed: false }])}
                  >
                    Split payment
                  </Button>
                  <p className="text-xs text-slate-500">
                    Tick a card, transfer or terminal payment as confirmed only once you have seen the slip or the bank alert;
                    a transfer also needs the reference on its alert, and one reference pays once.
                    Goods do not leave on an unconfirmed payment: hold the cart until it arrives, or sell on credit to a
                    named customer, when it is recorded as PENDING and the sale is unpaid until somebody confirms it.
                  </p>
                </fieldset>

                <dl className="space-y-1 text-sm">
                  {Number(tendered.pending) > 0 && <Row label="Pending confirmation" value={money(tendered.pending, currency)} />}
                  {Number(tendered.change) > 0 && <Row label="Change due" value={money(tendered.change, currency)} strong />}
                  {Number(tendered.short) > 0 && <Row label="Not covered" value={money(tendered.short, currency)} />}
                </dl>

                <CustomerSection value={customer} onChange={setCustomer} required={credit} canRegister={can('customers:create')} />

                {redemptionOn && customer.kind === 'existing' && loyalty.data && (
                  <div className="rounded-lg border border-slate-200 p-2 text-sm">
                    <p className="text-slate-700">
                      {loyalty.data.balance} loyalty point{loyalty.data.balance === 1 ? '' : 's'}
                      {loyalty.data.balance > 0 && <> · worth {money(loyalty.data.balanceValue, currency)}</>}
                    </p>
                    {loyalty.data.balance > 0 && nonMedicineInCart && (
                      <Input
                        label="Points to spend"
                        inputMode="numeric"
                        value={redeem}
                        onChange={(e) => setRedeem(e.target.value.replace(/\D/g, ''))}
                        hint="Only on non-medicines in this sale."
                      />
                    )}
                  </div>
                )}

                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={credit} onChange={(e) => setCredit(e.target.checked)} />
                  Sell on credit (leave a balance owing)
                </label>

                {needsRx.length > 0 && (
                  <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    {needsRx.length} line{needsRx.length === 1 ? '' : 's'} need an approved prescription.
                  </p>
                )}
                <ErrorLine error={checkout.error} />
                <Button
                  className="w-full"
                  size="lg"
                  loading={checkout.isPending}
                  disabled={
                    !online ||
                    !branchId ||
                    ((credit || customer.kind === 'new') && !isNamed(customer)) ||
                    (Number(tendered.short) > 0 && !credit) ||
                    // A confirmed transfer is believed on its alert's reference.
                    tenders.some((t) => t.confirmed && needsTransferReference(t.method) && !transferReferenceReady(t.reference))
                  }
                  onClick={submit}
                >
                  {checkout.isError ? 'Retry checkout' : `Take ${money(totals.total, currency)}`}
                </Button>
              </div>
            )}
          </Card>
        </div>
      )}

      {branchId && (
        <HoldModal
          open={holdOpen}
          onClose={() => setHoldOpen(false)}
          branchId={branchId}
          lines={cart}
          onHeld={() => {
            setHoldOpen(false);
            setCart([]);
          }}
        />
      )}
    </div>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }): ReactNode {
  return (
    <div className="flex justify-between gap-3">
      <dt className={cn('text-slate-500', strong && 'font-medium text-slate-900')}>{label}</dt>
      <dd className={cn('tabular-nums', strong ? 'text-base font-semibold text-slate-900' : 'text-slate-700')}>{value}</dd>
    </div>
  );
}

/**
 * Who is buying. A walk-in unless the cashier names someone: picked from the
 * customers on file, or registered here with a name and (ideally) a phone —
 * the server finds them by phone if they are already on file. Credit needs a
 * named customer; nothing else does.
 */
export type CustomerChoice =
  | { kind: 'walk_in' }
  | { kind: 'existing'; id: string; name: string; phone: string | null }
  | { kind: 'new'; name: string; phone: string };

export const WALK_IN: CustomerChoice = { kind: 'walk_in' };

export function isNamed(c: CustomerChoice): boolean {
  return c.kind === 'existing' || (c.kind === 'new' && c.name.trim().length > 0);
}

export function customerPayload(c: CustomerChoice): Record<string, unknown> {
  if (c.kind === 'existing') return { customerId: c.id };
  if (c.kind === 'new' && c.name.trim()) {
    return { newCustomer: { name: c.name.trim(), ...(c.phone.trim() ? { phone: c.phone.trim() } : {}) } };
  }
  return {};
}

export function CustomerSection({
  value,
  onChange,
  required,
  canRegister = true,
}: {
  value: CustomerChoice;
  onChange: (c: CustomerChoice) => void;
  required: boolean;
  /** `customers:create`. Without it a cashier can still pick someone on file. */
  canRegister?: boolean;
}): ReactNode {
  const path = useBusinessPath();
  const [term, setTerm] = useState('');
  const debounced = useDebounced(term);
  const found = useQuery({
    queryKey: ['customers', path(''), 'pos-pick', debounced],
    queryFn: () => api.get<{ data: Array<{ id: string; name: string; phone: string | null }> }>(path(`/customers${qs({ search: debounced, pageSize: 10 })}`)),
    enabled: value.kind === 'walk_in' && debounced.trim().length > 1,
  });

  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium text-slate-700">
        Customer {required ? <span className="text-amber-700">(required for credit)</span> : <span className="font-normal text-slate-500">(optional)</span>}
      </legend>

      {value.kind === 'existing' && (
        <div className="flex items-center justify-between gap-2 rounded-lg border border-brand-200 bg-brand-50 px-3 py-2 text-sm">
          <span className="text-brand-800">
            {value.name} {value.phone && <span className="text-brand-700/80">· {value.phone}</span>}
          </span>
          <button type="button" className="text-xs font-medium text-slate-600 hover:text-slate-900" onClick={() => onChange(WALK_IN)}>
            Change
          </button>
        </div>
      )}

      {value.kind === 'new' && (
        <div className="space-y-2 rounded-lg border border-slate-200 p-3">
          <Input label="Name" value={value.name} onChange={(e) => onChange({ ...value, name: e.target.value })} />
          {!value.name.trim() && <p className="text-xs text-amber-700">Enter their name, or cancel to sell as a walk-in.</p>}
          <Input
            label="Phone"
            placeholder="So they are recognised next time"
            value={value.phone}
            onChange={(e) => onChange({ ...value, phone: e.target.value })}
          />
          <p className="text-xs text-slate-500">If this phone is already on file, the sale goes to that customer.</p>
          <button type="button" className="text-xs font-medium text-slate-600 hover:text-slate-900" onClick={() => onChange(WALK_IN)}>
            Cancel — walk-in
          </button>
        </div>
      )}

      {value.kind === 'walk_in' && (
        <>
          <Input placeholder="Walk-in — or search name or phone" value={term} onChange={(e) => setTerm(e.target.value)} aria-label="Search customers" />
          {(found.data?.data ?? []).length > 0 && (
            <ul className="max-h-40 overflow-y-auto rounded-lg border border-slate-200">
              {found.data!.data.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange({ kind: 'existing', id: c.id, name: c.name, phone: c.phone });
                      setTerm('');
                    }}
                    className="w-full px-3 py-1.5 text-left text-sm hover:bg-slate-50"
                  >
                    {c.name} {c.phone && <span className="text-slate-500">· {c.phone}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {canRegister && (
            <button
              type="button"
              className="text-xs font-medium text-brand-700 hover:text-brand-800"
              onClick={() => {
                const digits = /^[+\d\s-]{5,}$/.test(term.trim());
                onChange({ kind: 'new', name: digits ? '' : term.trim(), phone: digits ? term.trim() : '' });
                setTerm('');
              }}
            >
              + Register a new customer
            </button>
          )}
        </>
      )}
    </fieldset>
  );
}

function PrescriptionPanel({
  rx,
  loading,
  onAdd,
  branchId,
}: {
  rx: Prescription | undefined;
  loading: boolean;
  onAdd: (item: Pick<MedicineListItem, 'id' | 'name' | 'rxClass'>, unit: UnitView, rxItemId: string) => void;
  branchId: string | null;
}): ReactNode {
  if (loading || !rx) return <Card><p className="text-sm text-slate-500">Loading prescription…</p></Card>;
  const dispensable = ['approved', 'partially_dispensed'].includes(rx.dispensingStatus);
  return (
    <Card>
      <CardHeader
        title={`Dispensing ${rx.reference}`}
        subtitle={`${rx.patientName}${rx.reviewedByName ? ` · approved by ${rx.reviewedByName}` : ''}`}
        action={<StatusBadge status={rx.dispensingStatus} />}
      />
      {rx.patientAllergies.length > 0 && (
        <p role="alert" className="mb-3 flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm font-semibold text-red-800">
          <AlertTriangle className="size-4" aria-hidden /> Allergies: {rx.patientAllergies.join(', ')}
        </p>
      )}
      {!dispensable && <p className="text-sm text-amber-700">This prescription cannot be dispensed ({rx.dispensingStatus.replace(/_/g, ' ')}).</p>}
      <ul className="divide-y divide-slate-100">
        {rx.items.map((item) => (
          <RxItemRow key={item.id} item={item} disabled={!dispensable} branchId={branchId} onAdd={onAdd} />
        ))}
      </ul>
    </Card>
  );
}

function RxItemRow({
  item,
  disabled,
  branchId,
  onAdd,
}: {
  item: Prescription['items'][number];
  disabled: boolean;
  branchId: string | null;
  onAdd: (item: Pick<MedicineListItem, 'id' | 'name' | 'rxClass'>, unit: UnitView, rxItemId: string) => void;
}): ReactNode {
  const med = usePharmacy<{ units: UnitView[] }>(`/medicines/${item.productId}`, {}, Boolean(branchId));
  const outstanding = item.outstandingQuantity ?? 0;
  return (
    <li className="py-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-medium text-slate-900">{item.productName}</p>
          <p className="text-xs text-slate-500">
            Approved {item.approvedQuantity ?? '—'} {item.baseUnitName} · outstanding {outstanding} {item.baseUnitName}
          </p>
          {item.approvedDirections && <p className="mt-1 text-xs italic text-slate-600">“{item.approvedDirections}”</p>}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {(med.data?.units ?? [])
            .filter((u) => u.sellable && u.price !== null && u.baseQuantity <= outstanding)
            .map((u) => (
              <Button
                key={u.id}
                size="sm"
                variant="secondary"
                disabled={disabled || outstanding <= 0}
                onClick={() => onAdd({ id: item.productId, name: item.productName, rxClass: item.rxClass }, u, item.id)}
              >
                + {u.name}
              </Button>
            ))}
        </div>
      </div>
    </li>
  );
}

function HeldCarts({ branchId, onResume }: { branchId: string; onResume: (lines: CartLine[]) => void }): ReactNode {
  const path = useBusinessPath();
  const held = usePharmacy<ListOf<{ id: string; label: string; lines: Array<{ productId?: string; unitId?: string; quantity: number; prescriptionItemId?: string }>; held_at: string; held_by: string | null }>>(
    '/held-carts',
    { branchId },
  );
  const discard = usePharmacyWrite<{ id: string }>('del', (b) => `/held-carts/${b.id}`);
  const [resumeError, setResumeError] = useState<unknown>(null);
  const rows = held.data?.data ?? [];
  if (!rows.length) return null;

  // A held cart stores what checkout needs; names and prices are read fresh, so a price changed meanwhile is the new price.
  async function resume(cart: (typeof rows)[number]) {
    try {
      const lines: CartLine[] = [];
      for (const l of cart.lines) {
        if (!l.productId || !l.unitId) continue;
        const med = await api.get<{ id: string; name: string; rxClass: string; units: UnitView[] }>(path(`/pharmacy/medicines/${l.productId}`));
        const unit = med.units.find((u) => u.id === l.unitId);
        if (!unit || unit.price === null) continue;
        lines.push({
          key: lineKey(unit.id, l.prescriptionItemId),
          productId: med.id,
          productName: med.name,
          rxClass: med.rxClass,
          unitId: unit.id,
          unitName: unit.name,
          unitPrice: unit.price,
          quantity: l.quantity,
          discount: '',
          ...(l.prescriptionItemId ? { prescriptionItemId: l.prescriptionItemId } : {}),
        });
      }
      onResume(lines);
      discard.mutate({ id: cart.id });
    } catch (error) {
      setResumeError(error);
    }
  }

  return (
    <Card>
      <CardHeader title="Held carts" subtitle="Parked sales at this location. Holding a cart reserves no stock." />
      <ul className="divide-y divide-slate-100">
        {rows.map((c) => (
          <li key={c.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
            <div>
              <p className="font-medium text-slate-900">{c.label}</p>
              <p className="text-xs text-slate-500">
                {c.lines.length} line{c.lines.length === 1 ? '' : 's'}
                {c.held_by ? ` · ${c.held_by}` : ''}
              </p>
            </div>
            <div className="flex gap-1.5">
              <Button size="sm" variant="secondary" onClick={() => void resume(c)}>
                Resume
              </Button>
              <Button size="sm" variant="ghost" onClick={() => discard.mutate({ id: c.id })}>
                Discard
              </Button>
            </div>
          </li>
        ))}
      </ul>
      <ErrorLine error={resumeError ?? discard.error} />
    </Card>
  );
}

function HoldModal({
  open,
  onClose,
  branchId,
  lines,
  onHeld,
}: {
  open: boolean;
  onClose: () => void;
  branchId: string;
  lines: CartLine[];
  onHeld: () => void;
}): ReactNode {
  const [label, setLabel] = useState('');
  const hold = usePharmacyWrite<Record<string, unknown>>('post', '/held-carts');
  return (
    <Modal open={open} onClose={onClose} title="Hold this cart">
      <div className="space-y-3">
        <Input label="Label" placeholder="e.g. Lady in blue, waiting for husband" value={label} onChange={(e) => setLabel(e.target.value)} />
        <ErrorLine error={hold.error} />
        <Button
          disabled={!label.trim()}
          loading={hold.isPending}
          onClick={() =>
            hold.mutate(
              {
                branchId,
                label: label.trim(),
                lines: lines.map((l) => ({
                  unitId: l.unitId,
                  quantity: l.quantity,
                  ...(l.prescriptionItemId ? { prescriptionItemId: l.prescriptionItemId } : {}),
                  productId: l.productId,
                })),
              },
              { onSuccess: () => { setLabel(''); onHeld(); } },
            )
          }
        >
          Hold cart
        </Button>
      </div>
    </Modal>
  );
}

function ReceiptPanel({
  result,
  currency,
  timezone,
  onNewSale,
}: {
  result: CheckoutResult;
  currency: string;
  timezone: string;
  onNewSale: () => void;
}): ReactNode {
  const receipt = usePharmacy<Receipt>(`/sales/${result.saleId}/receipt`);
  const decide = usePharmacyWrite<PendingDecisionBody>('post', (b) => `/pending-payments/${b.id}/decide`);
  const r = receipt.data;
  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_22rem]">
      <Card className="print:border-0 print:shadow-none">
        <div id="pharmacy-receipt" className="space-y-3 text-sm">
          {!r ? (
            <p className="text-slate-500">Loading receipt…</p>
          ) : (
            <>
              <div className="text-center">
                <p className="text-base font-semibold text-slate-900">{r.business_name}</p>
                <p className="text-xs text-slate-500">{[r.branch_name, r.branch_address, r.branch_phone].filter(Boolean).join(' · ')}</p>
                <p className="mt-1 text-xs text-slate-500">
                  {r.reference} · {dateTime(r.occurredAt, timezone)}
                  {r.cashier ? ` · ${r.cashier}` : ''}
                </p>
                {r.prescription_reference && <p className="text-xs text-slate-500">Prescription {r.prescription_reference}</p>}
              </div>
              <Table head={['Item', 'Qty', 'Price', 'Total']} minWidth={320}>
                {r.lines.map((l) => (
                  <tr key={l.sale_item_id}>
                    <Td>
                      {l.description}
                      {Number(l.discount) > 0 && <span className="block text-xs text-slate-500">discount {money(l.discount, currency)}</span>}
                    </Td>
                    <Td className="tabular-nums">{l.unit_quantity}</Td>
                    <Td className="tabular-nums">{money(l.unit_price, currency)}</Td>
                    <Td className="text-right tabular-nums">{money(l.line_total, currency)}</Td>
                  </tr>
                ))}
              </Table>
              <dl className="space-y-1">
                {Number(r.discount) > 0 && <Row label="Discount" value={`− ${money(r.discount, currency)}`} />}
                <Row label="Total" value={money(r.amount, currency)} strong />
                {Number(r.pointsRedeemed ?? 0) > 0 && (
                  <Row label="Loyalty points spent" value={`${r.pointsRedeemed} (${money(r.loyalty_discount ?? '0', currency)})`} />
                )}
                {Number(r.pointsEarned ?? 0) > 0 && <Row label="Loyalty points earned" value={String(r.pointsEarned)} />}
                {r.payments.map((p, i) => (
                  <Row key={i} label={`Paid · ${PAYMENT_METHOD_LABELS[p.method as PaymentMethod] ?? p.method}`} value={money(p.amount, currency)} />
                ))}
                {Number(result.changeDue) > 0 && <Row label="Change" value={money(result.changeDue, currency)} />}
                {Number(r.outstanding) > 0 && <Row label="Balance owing" value={money(r.outstanding, currency)} strong />}
              </dl>
              <p className="text-center text-xs text-slate-500">
                Payment: <StatusBadge status={r.paymentStatus} />
              </p>
            </>
          )}
        </div>
      </Card>
      <div className="space-y-4 print:hidden">
        {result.duplicate && (
          <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            This checkout was already recorded as {result.reference}. Nothing was charged twice.
          </p>
        )}
        {r && r.pendingPayments.some((p) => p.status === 'pending') && (
          <Card>
            <CardHeader title="Payments awaiting confirmation" subtitle="Confirm only when the approval or alert has been seen." />
            <ul className="space-y-2">
              {r.pendingPayments
                .filter((p) => p.status === 'pending')
                .map((p) => (
                  <li key={p.id} className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm">
                      {PAYMENT_METHOD_LABELS[p.method as PaymentMethod] ?? p.method} · {money(p.amount, currency)}
                    </span>
                    <PendingDecision payment={p} busy={decide.isPending} onDecide={(body) => decide.mutate(body)} />
                  </li>
                ))}
            </ul>
            <ErrorLine error={decide.error} />
          </Card>
        )}
        <div className="flex gap-2">
          <Button variant="secondary" icon={<Printer className="size-4" />} onClick={() => window.print()}>
            Print
          </Button>
          <Button onClick={onNewSale}>New sale</Button>
        </div>
      </div>
    </div>
  );
}

function TillControl({ branchId, currency, timezone }: { branchId: string; currency: string; timezone: string }): ReactNode {
  const { can, user } = useAuth();
  const tills = usePharmacy<ListOf<Till>>('/tills', { branchId }, can('pharmacy:till'));
  const open = (tills.data?.data ?? []).find((t) => t.status === 'open' && t.cashier_user_id === user?.id);
  const [openModal, setOpenModal] = useState(false);
  const [closeModal, setCloseModal] = useState(false);
  const [amount, setAmount] = useState('');
  const openTill = usePharmacyWrite<Record<string, unknown>>('post', '/tills');
  const closeTill = usePharmacyWrite<{ id: string; countedCash: string }, { expectedCash: string; countedCash: string; variance: string; byMethod: Record<string, { received: string; refunded: string; net: string }> }>(
    'post',
    (b) => `/tills/${b.id}/close`,
  );
  if (!can('pharmacy:till')) return null;
  return (
    <div className="flex items-center gap-2">
      {open ? (
        <>
          <Badge tone="success">Till open since {dateTime(open.opened_at, timezone)}</Badge>
          <Button size="sm" variant="secondary" onClick={() => { setAmount(''); closeTill.reset(); setCloseModal(true); }}>
            Close till
          </Button>
        </>
      ) : (
        <Button size="sm" variant="secondary" onClick={() => { setAmount(''); setOpenModal(true); }}>
          Open till
        </Button>
      )}
      <Modal open={openModal} onClose={() => setOpenModal(false)} title="Open till">
        <div className="space-y-3">
          <Input label="Opening float" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
          <ErrorLine error={openTill.error} />
          <Button loading={openTill.isPending} onClick={() => openTill.mutate({ branchId, openingFloat: amount || '0' }, { onSuccess: () => setOpenModal(false) })}>
            Open
          </Button>
        </div>
      </Modal>
      <Modal open={closeModal} onClose={() => setCloseModal(false)} title="Close till">
        {closeTill.data ? (
          <dl className="space-y-1 text-sm">
            <Row label="Expected cash" value={money(closeTill.data.expectedCash, currency)} />
            <Row label="Counted" value={money(closeTill.data.countedCash, currency)} />
            <Row label="Variance" value={money(closeTill.data.variance, currency)} strong />
            {Object.entries(closeTill.data.byMethod).map(([m, v]) => (
              <Row key={m} label={`${PAYMENT_METHOD_LABELS[m as PaymentMethod] ?? m} (net)`} value={money(v.net, currency)} />
            ))}
          </dl>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-slate-500">Count the cash first. The expected figure is shown after you submit, not before.</p>
            <Input label="Counted cash" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
            <ErrorLine error={closeTill.error} />
            <Button disabled={!amount} loading={closeTill.isPending} onClick={() => open && closeTill.mutate({ id: open.id, countedCash: amount })}>
              Close till
            </Button>
          </div>
        )}
      </Modal>
    </div>
  );
}

/**
 * One suggestion: what it is, whether it can be sold here and why not, its
 * price, and a button per selling unit when it can. An item that cannot be
 * sold is still shown — the cashier learns it is in the catalogue and why it
 * cannot go in the cart.
 */
function TillSuggestion({
  item,
  active,
  currency,
  onHover,
  onAdd,
}: {
  item: TillItem;
  active: boolean;
  currency: string;
  onHover: () => void;
  onAdd: (unit: TillItem['units'][number]) => void;
}): ReactNode {
  const units = item.sellable ? item.units.filter((u) => u.sellable && u.price !== null) : [];
  return (
    <li
      id={`till-option-${item.id}`}
      role="option"
      aria-selected={active}
      onMouseEnter={onHover}
      className={cn('flex flex-col gap-2 px-2 py-3 sm:flex-row sm:items-center sm:justify-between', active && 'bg-slate-50')}
    >
      <div className="min-w-0">
        <p className="flex flex-wrap items-center gap-2 font-medium text-slate-900">
          <span className="truncate">{item.name}</span>
          {item.rxClass && <RxBadge rxClass={item.rxClass} />}
          <Badge tone={tillStateTone(item.state)}>{tillStateLabel(item.state)}</Badge>
        </p>
        <p className="text-xs text-slate-500">
          {[item.genericName, item.strength, item.dosageForm, item.sku && `SKU ${item.sku}`, item.categoryName].filter(Boolean).join(' · ') ||
            '—'}
        </p>
        <p className="text-xs text-slate-500">
          {item.price !== null ? money(item.price, currency) : 'No price'}
          {item.saleableOnHand !== null && (
            <>
              {' · '}In stock here:{' '}
              <span className={cn('font-medium', item.saleableOnHand > 0 ? 'text-slate-700' : 'text-red-600')}>
                {item.units.length ? describeQuantity(item.saleableOnHand, item.units) : item.saleableOnHand}
              </span>
            </>
          )}
          {item.nearestExpiry && <> · next expiry {item.nearestExpiry}</>}
        </p>
        {!item.sellable && item.reason && <p className="mt-0.5 text-xs text-amber-700">{item.reason}</p>}
      </div>
      {units.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {units.map((u) => (
            <Button key={u.id} size="sm" variant="secondary" onClick={() => onAdd(u)}>
              {u.name} · {money(u.price, currency)}
            </Button>
          ))}
        </div>
      )}
    </li>
  );
}
